// Payroll calculation (AD calendar months, day-prorated salary changes).
// Mirrors public.compute_salary_month() in migrations/005_payroll_bonus.sql —
// the database function is authoritative when a month is finalized; this copy
// drives on-screen previews and lists.
//
//   day rate  = monthly salary in effect that day / days in month
//   gross     = Σ day rates over the whole month
//   deduction = Σ day rates on absent days (up to today)
//   payable   : gross = round(gross), net = round(gross − deduction),
//               deduction = gross − net (rounded once, at the end; exact values kept)

export const pad2 = (n) => String(n).padStart(2, "0");
export const ymd = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
export const daysInMonth = (y, m) => new Date(y, m, 0).getDate();

// Rows for one staff member, oldest first.
export function historyFor(history, staffId) {
    return (history || [])
        .filter(h => h.staff_id === staffId)
        .sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
}

export function salaryRowOn(rows, dateStr) {
    let found = null;
    for (const r of rows) {
        if (r.effective_from <= dateStr && (!r.effective_to || r.effective_to >= dateStr)) found = r;
    }
    return found;
}

// The row in effect today (or the earliest future row if none yet).
export function currentSalaryRow(rows, today) {
    return salaryRowOn(rows, today) || null;
}

export function upcomingSalaryRow(rows, today) {
    return rows.find(r => r.effective_from > today) || null;
}

// Employee-level payroll policy (Admin-only fields on staff, audited in the DB).
// All payroll rules read this one object — no per-person special cases elsewhere.
export function payrollPolicy(staff) {
    return {
        terminated: staff?.employment_status === "terminated",
        endDate: staff?.employment_end_date || null,           // last working day
        attendanceAffectsPayroll: staff?.attendance_affects_payroll !== false,
        isTest: staff?.is_test_account === true,
    };
}

/**
 * @param rows          salary history rows for the staff member (any order)
 * @param statusForDate (dateStr) => attendance status, same as getStatusForDate()
 */
export function computeMonthPayroll({ rows, year, month, today, statusForDate, policy = {} }) {
    const endDate = policy.endDate || null;
    const deducts = policy.attendanceAffectsPayroll !== false;
    const sorted = [...rows].sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
    const dim = daysInMonth(year, month);
    const periods = [];
    const absentBySalary = new Map();
    let present = 0, absent = 0, holidays = 0, saturdays = 0, unsalariedDays = 0, daysAfterEnd = 0, needsReview = false;

    for (let d = 1; d <= dim; d++) {
        const date = ymd(year, month, d);
        if (endDate && date > endDate) { daysAfterEnd++; continue; } // employment ended
        const row = salaryRowOn(sorted, date);
        const monthly = row ? Number(row.monthly_salary) : 0;
        if (!row) unsalariedDays++;
        else {
            if (row.review_status === "unverified") needsReview = true;
            const last = periods[periods.length - 1];
            if (last && last.salary_history_id === (row.id ?? null) && last.monthly_salary === monthly) {
                last.to = date; last.days++;
            } else {
                periods.push({ salary_history_id: row.id ?? null, monthly_salary: monthly, from: date, to: date, days: 1 });
            }
        }
        if (date > today) continue;
        const st = statusForDate(date);
        if (st === "present") present++;
        else if (st === "saturday") saturdays++;
        else if (st === "holiday") holidays++;
        else if (st === "absent" || st === "auto_absent") {
            absent++;
            if (row && deducts) absentBySalary.set(monthly, (absentBySalary.get(monthly) || 0) + 1);
        }
    }

    for (const p of periods) p.amount = Math.round((p.monthly_salary * p.days / dim) * 100) / 100;
    const grossRaw = periods.reduce((a, p) => a + p.monthly_salary * p.days / dim, 0);
    let dedRaw = 0;
    absentBySalary.forEach((count, monthly) => { dedRaw += monthly * count / dim; });
    // Round once, at the final monetary figures (same as compute_salary_month).
    const grossSalary = Math.round(grossRaw);
    const netSalary = Math.round(grossRaw - dedRaw);
    const deduction = grossSalary - netSalary;

    return {
        daysInMonth: dim,
        workingDays: dim,
        grossExact: grossRaw,
        deductionExact: dedRaw,
        grossSalary,
        deduction,
        netSalary,
        present, absent, holidays, saturdays, unsalariedDays, daysAfterEnd,
        attendanceAffectsPayroll: deducts,
        periods,
        needsReview,
        perDaySalary: periods.length === 1 ? Math.round((periods[0].monthly_salary / dim) * 100) / 100 : null,
    };
}

/**
 * Outstanding (unpaid, finished) payroll months, newest last.
 * ctx: { staff, salaryPayments, salaryRowsFor(staffId), hasAttendanceInMonth(staffId,y,m), statusForDate(staffId,date), today }
 */
export function computePendingPayroll(ctx, filterStaffId = null) {
    const now = new Date();
    const curY = now.getFullYear(), curM = now.getMonth() + 1;
    const results = [];
    const staffList = ctx.staff.filter(s =>
        s.employment_type !== "freelancer" &&
        (filterStaffId ? s.id === filterStaffId : true) &&
        ctx.salaryRowsFor(s.id).length > 0
    );
    for (const s of staffList) {
        const policy = payrollPolicy(s);
        if (policy.isTest && !filterStaffId) continue;          // test accounts are never real outstanding payroll
        if (policy.terminated && !policy.endDate) continue;     // final working day not confirmed → no new payroll
        if (!s.join_date) continue;
        const jd = new Date(s.join_date);
        if (isNaN(jd)) continue;
        let y = jd.getFullYear(), m = jd.getMonth() + 1;
        const rows = ctx.salaryRowsFor(s.id);
        const endKey = policy.endDate ? Number(policy.endDate.slice(0, 4)) * 100 + Number(policy.endDate.slice(5, 7)) : Infinity;
        while ((y < curY || (y === curY && m < curM)) && y * 100 + m <= endKey) {
            const paid = ctx.salaryPayments.some(p => p.staff_id === s.id && p.month === m && p.year === y && p.status === "paid");
            // Salary that does not depend on attendance is due even in months without clock-ins.
            const due = !policy.attendanceAffectsPayroll || ctx.hasAttendanceInMonth(s.id, y, m);
            if (!paid && due) {
                const sum = computeMonthPayroll({ rows, year: y, month: m, today: ctx.today, statusForDate: d => ctx.statusForDate(s.id, d), policy });
                if (sum.periods.length > 0) {
                    results.push({
                        id: `virtual_${s.id}_${y}_${m}`,
                        staff_id: s.id, staff_name: s.name, month: m, year: y,
                        amount: sum.netSalary,
                        present: sum.present, absent: sum.absent, holidays: sum.holidays, saturdays: sum.saturdays,
                        deduction: sum.deduction, grossSalary: sum.grossSalary, periods: sum.periods,
                        needsReview: sum.needsReview, attendanceAffectsPayroll: sum.attendanceAffectsPayroll,
                        isTest: policy.isTest,
                        status: "pending", isVirtual: true,
                    });
                }
            }
            m++; if (m > 12) { m = 1; y++; }
        }
    }
    return results.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));
}

// Figures for a paid row: the finalized snapshot when present, otherwise
// (legacy rows paid before finalization existed) a live recalculation.
export function paidRowFigures(row, recalc) {
    if (row.calc_snapshot) {
        const s = row.calc_snapshot;
        return {
            source: "finalized",
            grossSalary: Number(s.gross_salary), deduction: Number(s.deduction), netSalary: Number(s.net_salary),
            present: s.present_days, absent: s.absent_days, holidays: s.holiday_days, saturdays: s.saturday_days,
            daysInMonth: s.days_in_month, periods: s.periods || [], employee: s.employee || null,
        };
    }
    const r = recalc ? recalc() : null;
    return r ? { source: "legacy", ...r, employee: null } : { source: "legacy", periods: [] };
}
